/**
 * The views behind the tabs of a document result: raw fields by source, mapped data, MRZ, VIZ,
 * normalised data, the mapping trace, face and signature. Tables go in the datagrid frame; on a
 * phone a wide one scrolls sideways inside it.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box, Button, Chip, Stack, TableBody, TableCell, TableHead, TableRow, Typography, useTheme,
} from '@mui/material';
import { DownloadRounded as DownloadIcon } from '@mui/icons-material';
import { DataTable } from '@/components/shell';
import { CodeBlock } from '@/components/shell';
import { asImageSrc, floatsToBase64, parseFieldName } from './testUtils';

export interface ScoredField { key: string; value: string; score: number | undefined }

/** Raw fields grouped by the first part of their name (MRZ, VIZ, NORM, QR…). */
export const groupBySource = (keyValues: Record<string, string>, keyScores: Record<string, number>) => {
  const grouped: Record<string, ScoredField[]> = {};
  for (const [key, value] of Object.entries(keyValues)) {
    const source = key.split('_')[0];
    if (!grouped[source]) grouped[source] = [];
    grouped[source].push({ key, value, score: keyScores[key] });
  }
  return grouped;
};

/** The string and number values of a response object, as table rows. */
export const valueEntries = (data: Record<string, unknown> | null | undefined): Array<[string, string]> =>
  data
    ? Object.entries(data)
      .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
      .map(([k, v]) => [k, String(v)] as [string, string])
    : [];

export const EmptyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{children}</Typography>
);

/** A confidence score: plain when high, a chip when it needs a second look. */
const Score: React.FC<{ score: number }> = ({ score }) => {
  const label = score.toFixed(2);
  if (score >= 0.95) return <>{label}</>;
  return <Chip label={label} size="small" color={score >= 0.8 ? 'warning' : 'error'} />;
};

const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return <Box component="span" sx={{ fontFamily: theme.custom.text.mono, overflowWrap: 'anywhere' }}>{children}</Box>;
};

const FieldsTable: React.FC<{ label: string; fields: ScoredField[] }> = ({ label, fields }) => {
  const t = useTranslate();
  return (
    <DataTable label={label}>
      <TableHead>
        <TableRow>
          <TableCell>{t('test.field')}</TableCell>
          <TableCell>{t('test.value')}</TableCell>
          <TableCell align="right">{t('screens.testPage.score')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {fields.map((f, i) => {
          const parsed = parseFieldName(f.key);
          const origin = [parsed.side, parsed.engine && parsed.engine !== parsed.source ? parsed.engine : null].filter(Boolean).join(' · ');
          return (
            <TableRow key={i}>
              <TableCell>
                <Box>{parsed.field}</Box>
                {origin && <Typography variant="caption" color="text.secondary">{origin}</Typography>}
              </TableCell>
              <TableCell sx={{ wordBreak: 'break-all' }}>
                {f.value ? (f.value.includes('*') ? <Mono>{f.value}</Mono> : f.value) : '—'}
              </TableCell>
              <TableCell align="right">{f.score !== undefined ? <Score score={f.score} /> : '—'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </DataTable>
  );
};

/** Every raw field, one table per source. */
export const GroupedFieldsView: React.FC<{ grouped: Record<string, ScoredField[]> }> = ({ grouped }) => {
  const t = useTranslate();
  if (Object.keys(grouped).length === 0) return <EmptyNote>{t('test.no_data')}</EmptyNote>;
  return (
    <Stack spacing={2.5}>
      {Object.entries(grouped).map(([source, fields]) => (
        <Box key={source}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{source}</Typography>
          <FieldsTable label={source} fields={fields} />
        </Box>
      ))}
    </Stack>
  );
};

/** Field name and value, as the integration receives them. */
export const KeyValueTable: React.FC<{ label: string; entries: Array<[string, string]> }> = ({ label, entries }) => {
  const t = useTranslate();
  return (
    <DataTable label={label}>
      <TableHead>
        <TableRow>
          <TableCell>{t('test.field')}</TableCell>
          <TableCell>{t('test.value')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {entries.map(([k, v], i) => (
          <TableRow key={i}>
            <TableCell>{k}</TableCell>
            <TableCell sx={{ overflowWrap: 'anywhere' }}>{v}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </DataTable>
  );
};

export const MrzView: React.FC<{ pages: any[] }> = ({ pages }) => {
  const t = useTranslate();
  return (
    <Stack spacing={2.5}>
      {pages.map((page, idx) => (
        <Stack key={idx} spacing={1}>
          {page.description && <Typography variant="subtitle2">{page.description}</Typography>}
          {page.lines?.length > 0 && (
            <CodeBlock spaced text={page.lines.map((l: any) => (typeof l === 'string' ? l : l.text || '')).join('\n')} />
          )}
          <DataTable label="MRZ">
            <TableHead>
              <TableRow>
                <TableCell>{t('test.field')}</TableCell>
                <TableCell>{t('test.value')}</TableCell>
                <TableCell>{t('test.valid')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {page.fields.map((f: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>{f.name}</TableCell>
                  <TableCell><Mono>{f.value}</Mono></TableCell>
                  <TableCell>
                    {f.valid === true
                      ? t('screens.testPage.yes')
                      : f.valid === false ? <Chip label={t('screens.testPage.no')} size="small" color="error" /> : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        </Stack>
      ))}
    </Stack>
  );
};

export const VizView: React.FC<{ pages: any[] }> = ({ pages }) => {
  const t = useTranslate();
  return (
    <Stack spacing={2.5}>
      {pages.map((page, idx) => (
        <Stack key={idx} spacing={1}>
          {page.class_Name && (
            <Typography variant="subtitle2">{`${page.class_Name} (${(page.class_Similarity * 100).toFixed(1)}%)`}</Typography>
          )}
          <DataTable label="VIZ">
            <TableHead>
              <TableRow>
                <TableCell>{t('test.field')}</TableCell>
                <TableCell>{t('test.value')}</TableCell>
                <TableCell align="right">{t('screens.testPage.score')}</TableCell>
                <TableCell>{t('test.language')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {page.fields.filter((f: any) => f.ocrable !== false || f.ocr_Text).map((f: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>{f.name}</TableCell>
                  <TableCell><Mono>{f.ocr_Text || '—'}</Mono></TableCell>
                  <TableCell align="right">{f.ocr_Score > 0 ? <Score score={f.ocr_Score} /> : '—'}</TableCell>
                  <TableCell>{f.ocr_Lang || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        </Stack>
      ))}
    </Stack>
  );
};

export const NormView: React.FC<{ pages: any[] }> = ({ pages }) => {
  const t = useTranslate();
  return (
    <Stack spacing={2.5}>
      {pages.map((page, idx) => (
        <Stack key={idx} spacing={1}>
          {page.description && <Typography variant="subtitle2">{page.description}</Typography>}
          <DataTable label="NORM">
            <TableHead>
              <TableRow>
                <TableCell>{t('test.field')}</TableCell>
                <TableCell>{t('test.value')}</TableCell>
                <TableCell>{t('test.language')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {page.fields.map((f: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>{f.name}</TableCell>
                  <TableCell>{f.value}</TableCell>
                  <TableCell>{f.ocr_Lang || f.lang || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        </Stack>
      ))}
    </Stack>
  );
};

export const DebugView: React.FC<{ entries: any[] }> = ({ entries }) => {
  const t = useTranslate();
  if (entries.length === 0) return <EmptyNote>{t('test.no_data')}</EmptyNote>;
  return (
    <DataTable label={t('test.mapping_debug')}>
      <TableHead>
        <TableRow>
          <TableCell>{t('test.field')}</TableCell>
          <TableCell>{t('test.value')}</TableCell>
          <TableCell>{t('test.source')}</TableCell>
          <TableCell>{t('test.reason')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {entries.map((d: any, i: number) => (
          <TableRow key={i}>
            <TableCell>{d.Field}</TableCell>
            <TableCell>{d.Value}</TableCell>
            <TableCell>{d.Source}</TableCell>
            <TableCell>{d.Reason}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </DataTable>
  );
};

/** The face vector as a QR code (float bytes in base64), with a download. */
const FaceVectorQR: React.FC<{ data: any }> = ({ data }) => {
  const t = useTranslate();
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);

  let encoded = '';
  if (Array.isArray(data)) encoded = floatsToBase64(data);
  else if (typeof data === 'string') {
    try { encoded = floatsToBase64(JSON.parse(data)); } catch { encoded = data.substring(0, 2000); }
  }

  useEffect(() => {
    if (!encoded || !canvasRef.current) return;
    setReady(false); setError(false);
    import('qrcode').then(QRCode => {
      QRCode.toCanvas(canvasRef.current!, encoded, {
        width: 200, margin: 1, errorCorrectionLevel: 'L',
      }, (err) => {
        if (err) { setError(true); console.warn('QR too large:', err); }
        else setReady(true);
      });
    });
  }, [encoded]);

  const download = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    link.download = 'face-vector-qr.png';
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
  };

  if (error) return <Typography variant="body2" color="text.secondary">{t('screens.testPage.qrTooLarge')}</Typography>;

  return (
    <Stack spacing={1} alignItems="flex-start">
      <canvas ref={canvasRef} style={{ borderRadius: theme.custom.radius.small, display: ready ? 'block' : 'none' }} />
      {ready && (
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={download}>
          {t('test.face_recognition.download_qr')}
        </Button>
      )}
    </Stack>
  );
};

export const FaceView: React.FC<{ face: string; signature: string; faceMatch: any; faceVector: any }> = ({
  face, signature, faceMatch, faceVector,
}) => {
  const t = useTranslate();
  const theme = useTheme();
  const imageSx = { maxWidth: '100%', borderRadius: theme.custom.radius.small, border: `1px solid ${theme.palette.divider}`, display: 'block' };

  if (!face && !signature && !faceVector) return <EmptyNote>{t('test.no_face_signature')}</EmptyNote>;

  const vectorText = typeof faceVector === 'string' ? faceVector : JSON.stringify(faceVector);

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, flexWrap: 'wrap' }}>
        {face && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>{t('test.face')}</Typography>
            <Box component="img" src={asImageSrc(face)} alt={t('screens.testPage.facePhoto')} sx={{ ...imageSx, maxHeight: 200 }} />
          </Box>
        )}
        {signature && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>{t('test.signature')}</Typography>
            <Box component="img" src={asImageSrc(signature)} alt={t('test.signature')} sx={{ ...imageSx, maxHeight: 120 }} />
          </Box>
        )}
        {faceMatch && (
          <Typography variant="body2">
            {t('screens.testPage.faceMatchDistance', { value: faceMatch.distance ?? t('screens.testPage.notAvailable') })}
          </Typography>
        )}
      </Box>
      {faceVector && (
        <Stack spacing={1}>
          <Typography variant="subtitle2">{t('test.face_vector')}</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <CodeBlock text={`${vectorText.substring(0, 500)}...`} copyText={vectorText} />
            </Box>
            <FaceVectorQR data={faceVector} />
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
